/**
 * Demo credential store — CLIENT-SIDE ONLY.
 *
 * There is no auth backend yet (see src/proxy.ts and src/app/(auth)/actions.ts),
 * so accounts live in this browser's localStorage. Passwords are stored only as
 * a PBKDF2-SHA256 hash with a per-account salt — never in plain text — but this
 * is still demo-grade: anyone with the device can read localStorage, and
 * accounts do not sync across devices. Swap this for a real provider
 * (NextAuth / Clerk / Lucia) + a server database before going live; the form
 * call-sites stay the same.
 *
 * Login is keyed on the email address: you sign in with the email + password you
 * registered, and the reset flow updates that account's password.
 */

const ACCOUNTS_KEY = "tutorials:accounts";
const RESET_KEY = "tutorials:reset";
const RESET_TTL_MS = 1000 * 60 * 30; // 30 minutes

interface Account {
  email: string;
  name?: string;
  salt: string;
  hash: string;
}
type AccountMap = Record<string, Account>; // keyed by normalised email

interface ResetRecord {
  email: string; // normalised
  token: string;
  expires: number;
}
type ResetMap = Record<string, ResetRecord>; // keyed by normalised email

const norm = (email: string) => email.trim().toLowerCase();

function readAccounts(): AccountMap {
  try {
    return JSON.parse(localStorage.getItem(ACCOUNTS_KEY) || "{}") as AccountMap;
  } catch {
    return {};
  }
}

function writeAccounts(map: AccountMap) {
  localStorage.setItem(ACCOUNTS_KEY, JSON.stringify(map));
}

function toHex(buf: ArrayBuffer): string {
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

function randomHex(bytes: number): string {
  const arr = new Uint8Array(bytes);
  crypto.getRandomValues(arr);
  return Array.from(arr)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

function hexToBytes(hex: string): Uint8Array<ArrayBuffer> {
  const out = new Uint8Array(new ArrayBuffer(hex.length / 2));
  for (let i = 0; i < out.length; i++) {
    out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  }
  return out;
}

async function hashPassword(password: string, saltHex: string): Promise<string> {
  const key = await crypto.subtle.importKey(
    "raw",
    new TextEncoder().encode(password),
    "PBKDF2",
    false,
    ["deriveBits"],
  );
  const bits = await crypto.subtle.deriveBits(
    { name: "PBKDF2", salt: hexToBytes(saltHex), iterations: 100_000, hash: "SHA-256" },
    key,
    256,
  );
  return toHex(bits);
}

export function accountExists(email: string): boolean {
  return Boolean(readAccounts()[norm(email)]);
}

export async function registerAccount(
  email: string,
  name: string,
  password: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
  const accounts = readAccounts();
  const key = norm(email);
  if (accounts[key]) {
    return {
      ok: false,
      error: "An account with this email already exists. Try signing in instead.",
    };
  }
  const salt = randomHex(16);
  const hash = await hashPassword(password, salt);
  accounts[key] = { email: email.trim(), name: name.trim(), salt, hash };
  writeAccounts(accounts);
  return { ok: true };
}

export async function verifyCredentials(
  email: string,
  password: string,
): Promise<{ ok: true; name?: string } | { ok: false; error: string; noAccount?: boolean }> {
  const account = readAccounts()[norm(email)];
  if (!account) {
    return { ok: false, error: "No account found for this email address.", noAccount: true };
  }
  const hash = await hashPassword(password, account.salt);
  if (hash !== account.hash) {
    return { ok: false, error: "That email and password do not match." };
  }
  return { ok: true, name: account.name };
}

/**
 * Start a password reset. Returns a token if the email has an account (used to
 * build the reset link), or null if it does not — callers should show the same
 * "check your email" message either way so account existence is not revealed.
 */
const RESET_COOLDOWN_MS = 60 * 1000; // don't mint a fresh token within 60s

export function createPasswordReset(email: string): string | null {
  const key = norm(email);
  if (!readAccounts()[key]) return null;
  // Keyed by email so a reset request for one account never overwrites (and
  // silently invalidates) a still-pending token for a different account.
  const map = readResets();
  const existing = map[key];
  // Throttle: if a token was minted in the last 60s, reuse it rather than
  // spamming the inbox once a real email provider is attached.
  if (
    existing &&
    Date.now() - (existing.expires - RESET_TTL_MS) < RESET_COOLDOWN_MS
  ) {
    return existing.token;
  }
  const token = randomHex(24);
  map[key] = { email: key, token, expires: Date.now() + RESET_TTL_MS };
  writeResets(map);
  return token;
}

function readResets(): ResetMap {
  try {
    return JSON.parse(localStorage.getItem(RESET_KEY) || "{}") as ResetMap;
  } catch {
    return {};
  }
}

function writeResets(map: ResetMap) {
  localStorage.setItem(RESET_KEY, JSON.stringify(map));
}

/** Find a pending reset record by its token, across all accounts. */
function findReset(token: string): ResetRecord | null {
  for (const rec of Object.values(readResets())) {
    if (rec.token === token) return rec;
  }
  return null;
}

/** The email a valid, unexpired token belongs to (for the reset screen), else null. */
export function resetTokenEmail(token: string): string | null {
  const rec = findReset(token);
  if (rec && Date.now() <= rec.expires) return rec.email;
  return null;
}

export async function consumePasswordReset(
  token: string,
  newPassword: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
  const rec = findReset(token);
  if (!rec) {
    return { ok: false, error: "This reset link is invalid. Please request a new one." };
  }
  if (Date.now() > rec.expires) {
    return { ok: false, error: "This reset link has expired. Please request a new one." };
  }
  const accounts = readAccounts();
  const account = accounts[rec.email];
  if (!account) {
    return { ok: false, error: "That account no longer exists." };
  }
  account.salt = randomHex(16);
  account.hash = await hashPassword(newPassword, account.salt);
  accounts[rec.email] = account;
  writeAccounts(accounts);
  const map = readResets();
  delete map[rec.email];
  writeResets(map);
  return { ok: true };
}
